import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Button
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getCapturedImageDisplayUrl } from '../services/s3/captureUrlService';
import { Gender } from '@gamma/shared/attributes';
import { GenderLabels } from '@gamma/shared/labels';

/**
 * AssociatedPersonList - Compact table view of associated persons (unknowns) with infinite scroll and multi-criteria search
 */
export default function AssociatedPersonList({ associatedPersons, onPersonClick, onDelete }) {
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [displayedCount, setDisplayedCount] = useState(100);
  const loaderRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Handle search button click
  const handleSearch = () => {
    setActiveSearch(searchInput);
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Filter associated persons based on active search (multi-criteria)
  const filteredPersons = useMemo(() => {
    if (!activeSearch.trim()) {
      return associatedPersons;
    }

    // Split by comma and trim each criterion
    const criteria = activeSearch.split(',').map(c => c.trim().toLowerCase()).filter(c => c);

    if (criteria.length === 0) {
      return associatedPersons;
    }

    // Person must match ALL criteria (AND logic)
    return associatedPersons.filter(person => {
      return criteria.every(criterion => {
        return (
          person.person_id?.toLowerCase().includes(criterion) ||
          person.physical_attributes?.gender?.toLowerCase().includes(criterion) ||
          person.physical_attributes?.age_group?.toLowerCase().includes(criterion)
        );
      });
    });
  }, [associatedPersons, activeSearch]);

  // Reset displayed count when associatedPersons array or search changes
  useEffect(() => {
    console.log('[AssociatedPersonList] Filter applied - Total:', associatedPersons.length, 'Filtered:', filteredPersons.length, 'Search:', activeSearch);
    setDisplayedCount(100);
  }, [associatedPersons, activeSearch]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && displayedCount < filteredPersons.length) {
          const newCount = Math.min(displayedCount + 50, filteredPersons.length);
          console.log('[AssociatedPersonList] Loading more:', displayedCount, '→', newCount);
          setDisplayedCount(newCount);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [displayedCount, filteredPersons.length]);

  const displayedPersons = filteredPersons.slice(0, displayedCount);

  function getImageUrl(person) {
    if (!person.sightings || person.sightings.length === 0) return null;
    const s3Key = person.sightings[0].capture_s3_key;
    return getCapturedImageDisplayUrl(s3Key);
  }

  function formatDate(isoString) {
    if (!isoString) return '—';
    const date = new Date(isoString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${day} ${month} ${time}`;
  }

  function getSightingChipColor(count) {
    if (count >= 10) return 'success';
    if (count >= 5) return 'warning';
    return 'default';
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
      {/* Search Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search: male, 30-39  (comma-separated, matches all)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setActiveSearch('');
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper'
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSearch}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              borderRadius: 1
            }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </Paper>

      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          flex: 1,
          overflow: 'auto'
        }}
      >
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, fontSize: '0.8125rem' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 50 }}>
                Img
              </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary' }}>
              Person ID
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 90 }}>
              Sightings
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 110 }}>
              First Seen
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 110 }}>
              Last Seen
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 70 }}>
              Gender
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 70 }}>
              Age
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 80 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedPersons.map((person) => (
            <TableRow
              key={person.person_id}
              hover
              onClick={() => onPersonClick(person)}
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                '&:last-child td': { borderBottom: 0 }
              }}
            >
              {/* Image Thumbnail */}
              <TableCell sx={{ p: 0.5 }}>
                {getImageUrl(person) ? (
                  <Box
                    component="img"
                    src={getImageUrl(person)}
                    alt={person.person_id}
                    sx={{
                      width: 40,
                      height: 40,
                      objectFit: 'cover',
                      borderRadius: 0.5,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: 0.5,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>?</Typography>
                  </Box>
                )}
              </TableCell>

              {/* Person ID */}
              <TableCell>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    color: 'secondary.main'
                  }}
                >
                  {person.person_id}
                </Typography>
              </TableCell>

              {/* Sighting Count */}
              <TableCell align="center">
                <Chip
                  label={person.sighting_count || 0}
                  size="small"
                  color={getSightingChipColor(person.sighting_count)}
                  sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600 }}
                />
              </TableCell>

              {/* First Seen */}
              <TableCell>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                  {formatDate(person.first_seen)}
                </Typography>
              </TableCell>

              {/* Last Seen */}
              <TableCell>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                  {formatDate(person.last_seen)}
                </Typography>
              </TableCell>

              {/* Gender */}
              <TableCell>
                {person.physical_attributes?.gender ? (
                  <Chip
                    label={GenderLabels[person.physical_attributes.gender]?.charAt(0) || person.physical_attributes.gender.charAt(0)}
                    size="small"
                    color={person.physical_attributes.gender === Gender.MALE ? 'primary' : person.physical_attributes.gender === Gender.FEMALE ? 'secondary' : 'default'}
                    sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600, width: 28 }}
                  />
                ) : (
                  <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>—</Typography>
                )}
              </TableCell>

              {/* Age Group */}
              <TableCell>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    color: person.physical_attributes?.age_group ? 'text.primary' : 'text.disabled'
                  }}
                >
                  {person.physical_attributes?.age_group || '—'}
                </Typography>
              </TableCell>

              {/* Actions */}
              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                  <Tooltip title="View Details" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPersonClick(person);
                      }}
                      sx={{
                        '&:hover': { bgcolor: 'info.50', color: 'info.main' }
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(person);
                      }}
                      sx={{
                        '&:hover': { bgcolor: 'error.50', color: 'error.main' }
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Infinite scroll loader */}
      {displayedCount < filteredPersons.length && (
        <Box
          ref={loaderRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2
          }}
        >
          <CircularProgress size={24} />
          <Typography sx={{ ml: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
            Loading more... ({displayedCount} of {filteredPersons.length})
          </Typography>
        </Box>
      )}

      {/* Empty state when no results */}
      {filteredPersons.length === 0 && activeSearch && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            px: 2
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No associated persons found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No associated persons match: {activeSearch.split(',').map(c => c.trim()).filter(c => c).join(', ')}
          </Typography>
        </Box>
      )}
      </TableContainer>

      {/* Status bar */}
      <Paper
        elevation={0}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          px: 2,
          py: 1
        }}
      >
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          Showing {displayedCount} of {filteredPersons.length}
          {activeSearch && ` (${associatedPersons.length} total)`}
          {displayedCount < filteredPersons.length && ' - scroll down to load more'}
        </Typography>
      </Paper>
    </Box>
  );
}
